import { useCallback, useEffect, useRef, useState } from 'react';
import type { IngestBatch, IngestBatchCounts, IngestBatchSkipReason } from '@kennisbank/shared';
import { apiGet, apiPost } from '../../api/client.js';
import { setVisibilityAwareInterval } from '../../lib/visibility-aware-interval.js';
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  FolderOpenIcon,
  XIcon,
} from '../icons.js';

/**
 * Server-side progress for bulk uploads. Reads `GET /batches` (every
 * kb_admin sees every recent batch, so a folder started on one laptop is
 * visible from another and after a reload). Polls fast only while something
 * is in flight; GETs are not rate-limited and the endpoint is aggregation-
 * backed, but an idle page still has no reason to poll every few seconds.
 *
 * Batches this tab is still pushing bytes for get an extra "keep this tab
 * open" / "safe to close" line — that part is only knowable locally.
 */

interface BatchFailure {
  jobId: string;
  filename: string;
  errorCode: string | null;
}

export interface BatchProgress extends IngestBatch {
  counts: IngestBatchCounts;
  active: boolean;
  runningFiles: string[];
  failures: BatchFailure[];
}

/** What this browser tab knows about a batch it is uploading. */
export interface LocalBatchUpload {
  batchId: string;
  label: string;
  total: number;
  /** Uploaded + finalized — now the server's responsibility. */
  handedOver: number;
  failed: Array<{ filename: string; message: string }>;
  finished: boolean;
}

const FAST_POLL_MS = 4_000;
const IDLE_POLL_MS = 60_000;
const DISMISSED_KEY = 'kennisbank.dismissedBatches';

const SKIP_TEXT: Record<IngestBatchSkipReason, string> = {
  unsupported_type: 'File type not supported',
  too_large: 'Larger than 100 MB',
  empty: 'Empty file',
  identical_existing: 'Identical content already in the knowledgebase',
  duplicate_in_drop: 'Same content as another file in this upload',
  collision_skipped: 'Name already in use — skipped',
};

function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeDismissed(ids: Set<string>): void {
  try {
    // Only recent batches are listed, so a short tail is enough.
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids].slice(-50)));
  } catch {
    /* storage unavailable — dismissal just won't persist */
  }
}

export function BatchProgressPanel({
  localUploads,
  describeError,
  keepAsDownloadCodes,
  onBatchSettled,
}: {
  localUploads: LocalBatchUpload[];
  describeError: (code: string | null) => string;
  keepAsDownloadCodes: ReadonlySet<string>;
  /** Called when a batch transitions to not-active (refresh the doc list). */
  onBatchSettled: () => void;
}): JSX.Element | null {
  const [batches, setBatches] = useState<BatchProgress[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [keptJobIds, setKeptJobIds] = useState<Set<string>>(new Set());
  const activeIdsRef = useRef<Set<string>>(new Set());

  const localById = new Map(localUploads.map((l) => [l.batchId, l]));
  const anyLocalRunning = localUploads.some((l) => !l.finished);
  const anyActive = batches.some((b) => b.active) || anyLocalRunning;

  const load = useCallback(async () => {
    try {
      const res = await apiGet<{ batches: BatchProgress[] }>('/api/v1/admin/knowledgebase/batches');
      setLoadError(null);
      const wasActive = activeIdsRef.current;
      if (res.batches.some((b) => !b.active && wasActive.has(b.id))) onBatchSettled();
      activeIdsRef.current = new Set(res.batches.filter((b) => b.active).map((b) => b.id));
      setBatches(res.batches);
    } catch {
      setLoadError('Could not load upload progress. Retrying…');
    }
  }, [onBatchSettled]);

  // A new local batch appears → fetch right away instead of waiting a tick.
  const localIds = localUploads.map((l) => l.batchId).join(',');
  useEffect(() => {
    void load();
  }, [load, localIds]);

  useEffect(
    () => setVisibilityAwareInterval(() => void load(), anyActive ? FAST_POLL_MS : IDLE_POLL_MS),
    [load, anyActive],
  );

  const visible = batches.filter((b) => b.active || localById.has(b.id) || !dismissed.has(b.id));
  if (visible.length === 0 && !loadError) return null;

  const dismiss = (id: string): void => {
    const next = new Set(dismissed).add(id);
    setDismissed(next);
    writeDismissed(next);
  };
  const toggle = (id: string): void =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const keepAsDownload = async (jobId: string): Promise<void> => {
    try {
      await apiPost(`/api/v1/admin/knowledgebase/documents/${jobId}/keep-as-download`, {});
      setKeptJobIds((cur) => new Set(cur).add(jobId));
      onBatchSettled();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Could not keep the file as download.');
    }
  };

  return (
    <section className="mt-6 space-y-3" aria-label="Bulk upload progress">
      <h2 className="ov-h-section">Bulk uploads</h2>
      {loadError && <p className="ov-meta text-nebula-warning">{loadError}</p>}
      {visible.map((b) => {
        const local = localById.get(b.id);
        const c = b.counts;
        const total = Math.max(
          b.plannedCount,
          c.uploading + c.queued + c.running + c.succeeded + c.failed,
        );
        const pct = (n: number): string => `${total ? (n / total) * 100 : 0}%`;
        const isOpen = expanded.has(b.id);
        return (
          <div key={b.id} className="rounded-lg border border-nebula-border bg-nebula-surface p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <FolderOpenIcon size={16} className="flex-shrink-0 text-nebula-secondary" />
                <span className="ov-body-strong truncate">{b.label}</span>
                <span className="ov-meta flex-shrink-0">
                  {b.ring === 'ring0' ? 'Internal' : 'Product'} ·{' '}
                  {new Date(b.createdAt).toLocaleString('en-GB', {
                    timeZone: 'Africa/Johannesburg',
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </span>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                {b.active ? (
                  <span className="ov-meta text-nebula-secondary">In progress</span>
                ) : (
                  <span className="ov-meta flex items-center gap-1 text-nebula-success">
                    <CheckCircle2Icon size={14} /> Finished
                  </span>
                )}
                {!b.active && !local && (
                  <button
                    type="button"
                    onClick={() => dismiss(b.id)}
                    aria-label="Hide this batch"
                    className="rounded-md p-1 text-nebula-fg-4 hover:bg-nebula-gray-5 hover:text-nebula-fg-1"
                  >
                    <XIcon size={14} />
                  </button>
                )}
              </div>
            </div>

            {local && !local.finished && (
              <p className="mt-2 flex items-center gap-1.5 rounded-md bg-nebula-warning-bg px-3 py-1.5 text-xs text-nebula-warning">
                <AlertTriangleIcon size={14} />
                Uploading from this browser: {local.handedOver} / {local.total}. Keep this tab open
                until the upload finishes.
              </p>
            )}
            {local && local.finished && (
              <p className="mt-2 flex items-center gap-1.5 rounded-md bg-nebula-success-bg px-3 py-1.5 text-xs text-nebula-success">
                <CheckCircle2Icon size={14} />
                {local.handedOver === local.total
                  ? 'All files are on the server — you can close this tab. Indexing continues without it.'
                  : `${local.handedOver} of ${local.total} files are on the server — you can close this tab. ${local.failed.length} failed to upload (listed below).`}
              </p>
            )}

            <div
              className="mt-3 flex h-2 overflow-hidden rounded-full bg-nebula-gray-4"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={c.succeeded + c.failed}
            >
              <div className="bg-nebula-success" style={{ width: pct(c.succeeded) }} />
              <div className="bg-nebula-error" style={{ width: pct(c.failed) }} />
              <div
                className="animate-pulse bg-nebula-secondary"
                style={{ width: pct(c.running) }}
              />
              <div className="bg-nebula-secondary/30" style={{ width: pct(c.queued) }} />
            </div>
            <p className="ov-meta mt-2">
              {c.succeeded} indexed · {c.running} indexing · {c.queued} queued · {c.failed} failed
              {c.uploading > 0 ? ` · ${c.uploading} waiting for upload` : ''}
              {b.skipped.length > 0 ? ` · ${b.skipped.length} skipped before upload` : ''}
            </p>
            {b.runningFiles.length > 0 && (
              <p className="ov-meta mt-1 truncate">Now indexing: {b.runningFiles.join(', ')}</p>
            )}

            {(b.failures.length > 0 || b.skipped.length > 0 || (local?.failed.length ?? 0) > 0) && (
              <button
                type="button"
                onClick={() => toggle(b.id)}
                className="ov-meta mt-2 flex items-center gap-1 text-nebula-secondary hover:underline"
              >
                {isOpen ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
                Details
              </button>
            )}
            {isOpen && (
              <div className="mt-2 space-y-2 text-xs">
                {b.failures.length > 0 && (
                  <ul className="space-y-1 rounded-md bg-nebula-error-bg/60 p-2">
                    {b.failures.map((f) => (
                      <li key={f.jobId} className="flex items-center justify-between gap-2">
                        <span className="min-w-0 text-nebula-fg-2">
                          <span className="font-medium">{f.filename}</span> —{' '}
                          {describeError(f.errorCode)}
                        </span>
                        {f.errorCode &&
                          keepAsDownloadCodes.has(f.errorCode) &&
                          !keptJobIds.has(f.jobId) && (
                            <button
                              type="button"
                              onClick={() => void keepAsDownload(f.jobId)}
                              title="Keep the file as download only — the assistant will not search it."
                              className="flex-shrink-0 rounded-md border border-nebula-border bg-white px-2 py-0.5 text-nebula-secondary hover:bg-nebula-tint"
                            >
                              Download only
                            </button>
                          )}
                        {keptJobIds.has(f.jobId) && (
                          <span className="flex-shrink-0 text-nebula-success">
                            Kept as download
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {local && local.failed.length > 0 && (
                  <ul className="space-y-1 rounded-md bg-nebula-warning-bg p-2">
                    {local.failed.map((f, i) => (
                      <li key={i} className="text-nebula-fg-2">
                        <span className="font-medium">{f.filename}</span> — {f.message} Drop the
                        file again to retry.
                      </li>
                    ))}
                  </ul>
                )}
                {b.skipped.length > 0 && (
                  <ul className="max-h-40 space-y-1 overflow-y-auto rounded-md bg-nebula-gray-5 p-2">
                    {b.skipped.map((s, i) => (
                      <li key={i} className="text-nebula-fg-3">
                        {s.filename} — {SKIP_TEXT[s.reason]}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
